import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  Container,
  Heading,
  NavContainer,
  SwitchContainer,
} from 'components/saved-credits/SavedCreditsList/components';
import { Spin, Switch } from 'antd';
import { groupByCourse, groupBySourceInstitutions } from 'utils/credit';
import { useEffect, useMemo, useState } from 'react';

import ArticulationCreditItem from 'components/credits/credit-items/ArticulationCreditItem';
import BackToResultButton from 'components/common/BackToResultButton';
import { CreditType } from 'types/credit';
import FlexContainer from 'components/common/FlexContainer';
import PrecedentCreditItem from 'components/credits/credit-items/PrecedentCreditItem';
import useArticulations from 'hooks/use-articulations';
import useCreditBookmarks from 'hooks/use-credit-bookmarks';
import useFormSchema from 'hooks/use-form-schema';
import usePrecedents from 'hooks/use-precedents';
import { useQueryParams } from 'hooks/use-query-params';
import useSourceInstitutions from 'hooks/use-source-institutions';
import useTranslation from 'hooks/use-translation';

export default function SavedCreditsList(): JSX.Element {
  const query = useQueryParams();
  const sourceInstitutions = useSourceInstitutions();
  const [groupedByCourse, setGroupedByCourse] = useState(false);
  const translation = useTranslation();
  const { creditBookmarks } = useCreditBookmarks();
  const articulations = useArticulations();
  const precedents = usePrecedents();
  const formSchema = useFormSchema();

  useEffect(() => {
    const grouping = query.get('grouping');
    if (!grouping) {
      return;
    }
    if (grouping === 'course') {
      setGroupedByCourse(true);
    } else {
      setGroupedByCourse(false);
    }
  }, [query]);

  const groupedBookmarks = useMemo(() => {
    if (!formSchema) {
      return [];
    }
    if (groupedByCourse) {
      // this can either be name or ID
      const groupBy = translation?.saved.groupCourseBy ?? 'name';

      return groupByCourse(creditBookmarks, articulations, precedents, formSchema, groupBy);
    }
    return groupBySourceInstitutions(creditBookmarks, articulations, precedents, formSchema, sourceInstitutions);
  }, [articulations, creditBookmarks, formSchema, groupedByCourse, precedents, sourceInstitutions]);

  if (!translation) {
    return <Spin />;
  }

  return (
    <Container className='saved-credits-container'>
      <NavContainer className='saved-credits-nav-container' flexDirection='row' justifyContent='space-between'>
        <BackToResultButton />
        <SwitchContainer>
          <span>Grouped by {translation.institution} course</span>
          <Switch
            onChange={setGroupedByCourse}
            checked={groupedByCourse}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </SwitchContainer>
      </NavContainer>
      <FlexContainer gap={40} className='saved-credits-list'>
        {groupedBookmarks.map(([heading, creditItems]) => {
          return (
            <FlexContainer key={heading} gap={16}>
              <Heading className='source-institutions-heading'>{heading}</Heading>
              <FlexContainer>
                {creditItems.map((creditItem) => {
                  if (creditItem.type === CreditType.ARTICULATION) {
                    return (
                      <ArticulationCreditItem
                        key={creditItem.articulation.ID}
                        articulations={[creditItem.articulation]}
                      />
                    );
                  }
                  return <PrecedentCreditItem key={creditItem.precedent.ID} precedent={creditItem.precedent} />;
                })}
              </FlexContainer>
            </FlexContainer>
          );
        })}
      </FlexContainer>
    </Container>
  );
}
